/**
 * Home Board contracts (Slices 1–4).
 *
 * Re-exports the generated Zod schemas for the workspace home board surface
 * (`GET /v1/workspaces/{workspaceId}/home`). Per the OpenAPI-first contract
 * rule, no DTO is hand-written — every type is derived via
 * `z.infer<typeof zGenerated*>`.
 *
 * The envelope shape:
 *
 *   HomeBoardResponse = {
 *     workspace: HomeWorkspaceDTO,
 *     freshnessAt: ISODateTime,
 *     cacheTtlSeconds: number,
 *     degradeReasons?: string[],
 *     surfaces: HomeSurface[],         // discriminated union, server-ordered
 *   }
 *
 * Slices 1–4 ship twelve surface kinds total. Slice 3 adds the three
 * intelligence-engine surfaces (`fingerprint_status`,
 * `discovery_suggestions`, `discovery_saved_inbox`); Slice 4 adds
 * `intelligence_critical` and `intelligence_patterns`. Subsequent slices
 * may add additional kinds; the client must never re-sort `surfaces[]`
 * or render surfaces that aren't present in the response.
 */

import { z } from 'zod';

import {
  zActiveWorkPipelineStage,
  zActiveWorkSurface,
  zAttentionItem,
  zAttentionItemReason,
  zAttentionItemsSurface,
  zCriticalBlockerItem,
  zCriticalBlockersSurface,
  zConnectedArtifactItem,
  zCriticalInsightItem,
  zCriticalInsightType,
  zDiscoverySavedInboxSurface,
  zDiscoverySuggestionItem,
  zDiscoverySuggestionsSurface,
  zEvidenceHighlightItem,
  zEvidenceHighlightType,
  zEvidenceHighlightsSurface,
  zFingerprintStatusSurface,
  zHomeBoardResponse,
  zHomeWorkspaceDto,
  zIntelligenceCriticalSurface,
  zIntelligencePatternsSurface,
  zNotesActivityItem,
  zNotesActivitySurface,
  zNotesConnectedArtifactsSurface,
  zNotesUnconfirmedEdgesSurface,
  zObjectiveDto,
  zPatternItem,
  zPatternTier,
  zPatternType,
  zPinnedCollectionItem,
  zPinnedCollectionsSurface,
  zPipelinePulseSurface,
  zRecentConversationItem,
  zRecentConversationsSurface,
  zRecentReadyItem,
  zRecentReadySurface,
  zSavedInboxItem,
  zSavedInboxItemStatus,
  zSavedInboxSource,
  zUnconfirmedEdgeItem,
  zWorkspaceIdentitySurface,
} from '@/api/generated/hey-api/zod.gen';

// ─── Envelope ────────────────────────────────────────────────────────────────

export type HomeBoardResponse = z.infer<typeof zHomeBoardResponse>;
export type HomeWorkspaceDTO = z.infer<typeof zHomeWorkspaceDto>;
export type ObjectiveDTO = z.infer<typeof zObjectiveDto>;

// ─── Surface variants ────────────────────────────────────────────────────────

export type WorkspaceIdentitySurface = z.infer<typeof zWorkspaceIdentitySurface>;
export type PipelinePulseSurface = z.infer<typeof zPipelinePulseSurface>;
export type CriticalBlockersSurface = z.infer<typeof zCriticalBlockersSurface>;
export type AttentionItemsSurface = z.infer<typeof zAttentionItemsSurface>;
export type ActiveWorkSurface = z.infer<typeof zActiveWorkSurface>;
export type RecentReadySurface = z.infer<typeof zRecentReadySurface>;
export type EvidenceHighlightsSurface = z.infer<typeof zEvidenceHighlightsSurface>;

export type CriticalBlockerItem = z.infer<typeof zCriticalBlockerItem>;
export type AttentionItem = z.infer<typeof zAttentionItem>;
export type AttentionItemReasonType = z.infer<typeof zAttentionItemReason>;
export type ActiveWorkPipelineStage = z.infer<typeof zActiveWorkPipelineStage>;
export type RecentReadyItem = z.infer<typeof zRecentReadyItem>;
export type EvidenceHighlightItem = z.infer<typeof zEvidenceHighlightItem>;
export type EvidenceHighlightType = z.infer<typeof zEvidenceHighlightType>;

// ─── Slice 3 surface variants ────────────────────────────────────────────────

export type FingerprintStatusSurface = z.infer<typeof zFingerprintStatusSurface>;
export type DiscoverySuggestionsSurface = z.infer<typeof zDiscoverySuggestionsSurface>;
export type DiscoverySuggestionItem = z.infer<typeof zDiscoverySuggestionItem>;
export type DiscoverySavedInboxSurface = z.infer<typeof zDiscoverySavedInboxSurface>;
export type SavedInboxItem = z.infer<typeof zSavedInboxItem>;
export type SavedInboxItemStatus = z.infer<typeof zSavedInboxItemStatus>;
export type SavedInboxSource = z.infer<typeof zSavedInboxSource>;

// ─── Slice 4 surface variants ────────────────────────────────────────────────

export type IntelligenceCriticalSurface = z.infer<typeof zIntelligenceCriticalSurface>;
export type CriticalInsightItem = z.infer<typeof zCriticalInsightItem>;
export type CriticalInsightType = z.infer<typeof zCriticalInsightType>;
export type IntelligencePatternsSurface = z.infer<typeof zIntelligencePatternsSurface>;
export type PatternItem = z.infer<typeof zPatternItem>;
export type PatternType = z.infer<typeof zPatternType>;
export type PatternTier = z.infer<typeof zPatternTier>;

// ─── Slices 5–7 surface variants ────────────────────────────────────────────

export type RecentConversationsSurface = z.infer<typeof zRecentConversationsSurface>;
export type RecentConversationItem = z.infer<typeof zRecentConversationItem>;
export type RecentConversationScopeMode = NonNullable<RecentConversationItem['scopeMode']>;
export type NotesActivitySurface = z.infer<typeof zNotesActivitySurface>;
export type NotesActivityItem = z.infer<typeof zNotesActivityItem>;
export type NotesUnconfirmedEdgesSurface = z.infer<typeof zNotesUnconfirmedEdgesSurface>;
export type UnconfirmedEdgeItem = z.infer<typeof zUnconfirmedEdgeItem>;
export type UnconfirmedEdgeType = UnconfirmedEdgeItem['edgeType'];
export type UnconfirmedEdgeSource = UnconfirmedEdgeItem['source'];
export type NotesConnectedArtifactsSurface = z.infer<typeof zNotesConnectedArtifactsSurface>;
export type ConnectedArtifactItem = z.infer<typeof zConnectedArtifactItem>;
export type ConnectedArtifactKind = ConnectedArtifactItem['targetKind'];
export type PinnedCollectionsSurface = z.infer<typeof zPinnedCollectionsSurface>;
export type PinnedCollectionItem = z.infer<typeof zPinnedCollectionItem>;

/**
 * Union of all surface variants. The `kind` discriminator narrows at usage:
 *
 *   for (const surface of response.surfaces) {
 *     if (surface.kind === 'pipeline_pulse') { ... }
 *   }
 */
export type HomeSurface = HomeBoardResponse['surfaces'][number];

/** All surface `kind` literals. */
export type HomeSurfaceKind = HomeSurface['kind'];

/** Type-narrowing helpers — preferred over manual `kind === '…'` switches. */
export const isWorkspaceIdentity = (s: HomeSurface): s is WorkspaceIdentitySurface =>
  s.kind === 'workspace_identity';
export const isPipelinePulse = (s: HomeSurface): s is PipelinePulseSurface =>
  s.kind === 'pipeline_pulse';
export const isCriticalBlockers = (s: HomeSurface): s is CriticalBlockersSurface =>
  s.kind === 'critical_operational_blockers';
export const isAttentionItems = (s: HomeSurface): s is AttentionItemsSurface =>
  s.kind === 'standard_attention_items';
export const isActiveWork = (s: HomeSurface): s is ActiveWorkSurface => s.kind === 'active_work';
export const isRecentReady = (s: HomeSurface): s is RecentReadySurface => s.kind === 'recent_ready';
export const isEvidenceHighlights = (s: HomeSurface): s is EvidenceHighlightsSurface =>
  s.kind === 'evidence_highlights';
export const isFingerprintStatus = (s: HomeSurface): s is FingerprintStatusSurface =>
  s.kind === 'fingerprint_status';
export const isDiscoverySuggestions = (s: HomeSurface): s is DiscoverySuggestionsSurface =>
  s.kind === 'discovery_suggestions';
export const isDiscoverySavedInbox = (s: HomeSurface): s is DiscoverySavedInboxSurface =>
  s.kind === 'discovery_saved_inbox';
export const isIntelligenceCritical = (s: HomeSurface): s is IntelligenceCriticalSurface =>
  s.kind === 'intelligence_critical';
export const isIntelligencePatterns = (s: HomeSurface): s is IntelligencePatternsSurface =>
  s.kind === 'intelligence_patterns';
export const isRecentConversations = (s: HomeSurface): s is RecentConversationsSurface =>
  s.kind === 'recent_conversations';
export const isNotesActivity = (s: HomeSurface): s is NotesActivitySurface =>
  s.kind === 'notes_activity';
export const isNotesUnconfirmedEdges = (s: HomeSurface): s is NotesUnconfirmedEdgesSurface =>
  s.kind === 'notes_unconfirmed_edges';
export const isNotesConnectedArtifacts = (s: HomeSurface): s is NotesConnectedArtifactsSurface =>
  s.kind === 'notes_connected_artifacts';
export const isPinnedCollections = (s: HomeSurface): s is PinnedCollectionsSurface =>
  s.kind === 'pinned_collections';

// ─── Re-export Zod schemas under canonical names for runtime validation ──────

export {
  zHomeBoardResponse as HomeBoardResponseSchema,
  zHomeWorkspaceDto as HomeWorkspaceDTOSchema,
  zObjectiveDto as ObjectiveDTOSchema,
};
